import { useState, useEffect, useRef } from "react";
import { useContext, memo } from "react";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./DieselCooler.css";
import { WellContext } from "../context/WellContext";
import { ProcessContext } from "../context/ProcessContext";
import PumpFaceplate from "../components/PumpFaceplate";
import AirCooler from "../components/AirCooler";
import AirCoolerFaceplate from "../components/AirCoolerFaceplate";
import { AirCoolerContext } from "../context/AirCoolerContext";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";





import TransmitterFaceplate from "../components/TransmitterFaceplate";





function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}





function AirCoolerPage() {
  const [openPanels, setOpenPanels] = useState([]);

  const openPanel = (tag) => {
    setOpenPanels((prev) =>
      prev.includes(tag) ? prev : [...prev, tag]
    );
  };

  const closePanel = (tag) => {
    setOpenPanels((prev) => prev.filter((p) => p !== tag));
  };

  return (
    <div>
      <AirCooler tag="M103A_A" x={945} y={322}/>
      <AirCooler tag="M103A_B" x={988} y={322}/>
      <AirCooler tag="M103B_A" x={945} y={552}/>
      <AirCooler tag="M103B_B" x={988} y={552}/>
      <AirCooler tag="M103C_A" x={1180} y={322}/>
      <AirCooler tag="M103C_B" x={1223} y={322}/>
      {openPanels.map((tag, i) => (
        <AirCoolerFaceplate
          key={tag}
          tag={tag}
          close={() => closePanel(tag)}
          defaultPosition={{ x: 300 + i * 40, y: 120 + i * 40 }}
        />
      ))}
    </div>
  );
}



function DieselCooler() {
  // ✅ Use ref to track render count (for debugging only)
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`DieselCooler Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("DieselCooler");
  }, []);

  const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);

  const DEFAULT_VALVES = {
    FCV_3301: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_3302: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_3303: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("DieselCooler_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_3301: { ...DEFAULT_VALVES.FCV_3301, ...parsed.FCV_3301 },
        FCV_3302: { ...DEFAULT_VALVES.FCV_3302, ...parsed.FCV_3302 },
        FCV_3303: { ...DEFAULT_VALVES.FCV_3303, ...parsed.FCV_3303 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("DieselCooler_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= AIR COOLER PANEL =================
  const [openCoolerPanels, setOpenCoolerPanels] = useState([]);
  const openCoolerPanel = (tag) => {
    setOpenCoolerPanels((prev) =>
      prev.includes(tag) ? prev : [...prev, tag]
    );
  };
  const closeCoolerPanel = (tag) => {
    setOpenCoolerPanels((prev) => prev.filter((p) => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  const renderInput = ({
    name,
    value,
    setter,
    x,
    y,
    w,
    h,
    bg = "#222",
    color = "#222"
  }) => (
    <input
      value={value}
      onChange={(e) => setter(e.target.value)}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        backgroundColor: bg,
        color,
        border: "1px solid #777",
        borderRadius: "4px",
        padding: "1px 2px",
        fontSize: "14px",
        zIndex: 20,
        textAlign: "center"
      }}
    />
  );

  const A101A_Loovers =  processData.A101A_Loovers ?? 100;
  const A101B_Loovers =  processData.A101B_Loovers ?? 100;
  const A101C_Loovers =  processData.A101C_Loovers ?? 100;
  const ATM_Air =  processData.ATM_Air ?? 25;

  const setA101A_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101A_Loovers: Number(value)
  }));
};

const setA101B_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101B_Loovers: Number(value)
  }));
};

const setA101C_Loovers = (value) => {
  setProcessData(prev => ({
    ...prev,
    A101C_Loovers: Number(value)
  }));
};

const setATM_Air = (value) => {
  setProcessData(prev => ({
    ...prev,
    ATM_Air: Number(value)
  }));
};


  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const FIC_16009 = transmitters.FIC_16009 || 0;
  const PT_16002 = transmitters.PT_16002 || 0;
  const TT_16002 = transmitters.TT_16002 || 0;
  const TT_11001 = transmitters.TT_11001 || 0;
  const PT_2406 = transmitters.PT_2406 || 0;
  const FIC_2408 = transmitters.FIC_2408 || 0;
  const TT_11006 = transmitters.TT_11006 || 0;
  const PT_2308 = transmitters.PT_2308 || 0;
  const FIC_2308 = transmitters.FIC_2308 || 0;


/* ==========================================
   BACK PRESSURE FROM REFLUX DRUM
========================================== */

const BackPressureFactor =
Number(
  localStorage.getItem(
    "PF_BackPressure"
  )
) || 0;

localStorage.setItem(
  "BackPressureFactor",
  BackPressureFactor
);



  const xv3301Open = !valves?.XV_3301;
  const xv3302Open = !valves?.XV_3302;
  const xv3303Open = !valves?.XV_3303;
  const xv3304Open = !valves?.XV_3304;
  const xv3305Open = !valves?.XV_3305;
  const xv3306Open = !valves?.XV_3306;





  const FIC_3301 = FIC_2408;
  const TT_3301 = TT_11001;
  const PT_3301 = PT_2406 * 0.4;
  const PT_3302 = PT_3301 * 0.6;
  const TT_3304 = xv3301Open ? 27.564 : 22;



  

  // ======================================================
  // PRE FLASH OVERHEAD VAPOR AIR COOLER CONDENSING LOGIC
  // ======================================================
  const { coolerData } = useContext(AirCoolerContext);

  // OUTPUT TEMPERATURE
  const [TT_3302, setTT_3302] = useState(0);

  // ======================================================
  // GET MOTOR DATA
  // ======================================================
  const getMotor = (tag) => {
    return coolerData?.[tag] || { speed: 0, running: false };
  };

  const M103A_A = getMotor("M103A_A");
  const M103A_B = getMotor("M103A_B");
  const M103B_A = getMotor("M103B_A");
  const M103B_B = getMotor("M103B_B");
  const M103C_A = getMotor("M103C_A");
  const M103C_B = getMotor("M103C_B");


  // ======================================================
  // COOLER SPEED CALCULATION
  // ======================================================
  const getCoolerSpeed = (A, B) => {
    let speed = 0;
    if (A.running && B.running) {
      speed = (A.speed + B.speed) / 2;
    } else if (A.running) {
      speed = A.speed;
    } else if (B.running) {
      speed = B.speed;
    }
    return speed;
  };

  const A101Speed = getCoolerSpeed(M103A_A, M103A_B);
  const A102Speed = getCoolerSpeed(M103B_A, M103B_B);
  const A103Speed = getCoolerSpeed(M103C_A, M103C_B);

  // ======================================================
  // RUNNING STATUS
  // ======================================================
  const A101_ON = M103A_A.running || M103A_B.running;
  const A102_ON = M103B_A.running || M103B_B.running;
  const A103_ON = M103C_A.running || M103C_B.running;

  // ======================================================
  // LOUVERS
  // ======================================================
  const safeLouver = (value) => Math.max(0, Math.min(100, Number(value) || 0));

  const L101 = safeLouver(A101A_Loovers);
  const L102 = safeLouver(A101B_Loovers);
  const L103 = safeLouver(A101C_Loovers);

  // ======================================================
  // ATMOSPHERIC AIR EFFECT
  // ======================================================
  const atmFactor = Math.max(0.35, 1 - ((Number(ATM_Air) - 25) / 50));

  // ======================================================
  // COOLING DUTY
  // ======================================================
  const duty = (running, speed, louver) => {
    if (!running) return 0;
    const motorCapacity = 28;
    const louverFactor = louver / 100;
    return motorCapacity * (speed / 100) * louverFactor * atmFactor;
  };

  const totalCooling =
    duty(A101_ON, A101Speed, L101) +
    duty(A102_ON, A102Speed, L102) +
    duty(A103_ON, A103Speed, L103)

  // ======================================================
  // TT-3302
  // ======================================================
  const newTT3302 = Number(TT_3301) - totalCooling;

  // ✅ FIXED: Use refs to track values and prevent re-renders
  const TT_3302_Ref = useRef(TT_3302);
  const TT_3303_Ref = useRef(0);
  const TT_3305_Ref = useRef(0);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(false);

  // ✅ Update TT_3302 when dependencies change
  useEffect(() => {
    const calculated = Math.max(newTT3302, Number(ATM_Air));
    TT_3302_Ref.current = calculated;
    setTT_3302(calculated);
  }, [TT_3301, ATM_Air, A101Speed, A102Speed, A103Speed, L101, L102, L103]);

  
 
 
 
 
 /* =========================================================
   E-113 TAGS
========================================================= */

const TT_3303 = processData.TT_3303;
const TT_3305 = processData.TT_3305;


/* =========================================================
   E-113 REFS
========================================================= */

const E113_intervalRef = useRef(null);
const E113_isMounted = useRef(false);
const E113_processRef = useRef({});


/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E113_processRef.current = {

    TT_3302,
    TT_3304

  };

});


/* =========================================================
   E-113 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E113_isMounted.current) return;

  E113_isMounted.current = true;

  console.log("✅ E-113 interval created");

  E113_intervalRef.current = setInterval(() => {

    const {

      TT_3302,
      TT_3304

    } = E113_processRef.current;


    /* =====================================
       INLET TEMPERATURES
    ===================================== */

    const stream1 =
      Number(TT_3302) || 0;

    const stream2 =
      Number(TT_3304) || 0;


    let targetTT3303 = stream1;
    let targetTT3305 = stream2;


    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    const hotter =
      Math.max(stream1, stream2);

    const colder =
      Math.min(stream1, stream2);

    const deltaT =
      hotter - colder;


    if (deltaT > 0.05) {

      /*
      Always exchange heat
      */

      const duty =
        Math.max(
          0.30,
          deltaT * 0.22
        );

      const secondaryDuty =
        duty * 0.70;

      if (stream1 >= stream2) {

        targetTT3303 =
          stream1 - duty;

        targetTT3305 =
          stream2 + secondaryDuty;

      }

      else {

        targetTT3303 =
          stream1 + secondaryDuty;

        targetTT3305 =
          stream2 - duty;

      }

    }


    /* =====================================
       PHYSICAL LIMITS
    ===================================== */

    if (stream1 >= stream2) {

      targetTT3303 =
        Math.max(
          targetTT3303,
          stream2 + 0.5
        );

      targetTT3305 =
        Math.min(
          targetTT3305,
          stream1 - 0.5
        );

    }

    else {

      targetTT3305 =
        Math.max(
          targetTT3305,
          stream1 + 0.5
        );

      targetTT3303 =
        Math.min(
          targetTT3303,
          stream2 - 0.5
        );

    }


    /* =====================================
       UPDATE PROCESS DATA
    ===================================== */

    setProcessData(prev => {

      /*
      Initialize outlet temperatures.
      Ignore bogus startup values like
      0, 0.003, null, undefined.
      */

      const currentTT3303 =

        (typeof prev.TT_3303 === "number" &&
         prev.TT_3303 > 5)

        ? prev.TT_3303

        : stream1;


      const currentTT3305 =

        (typeof prev.TT_3305 === "number" &&
         prev.TT_3305 > 5)

        ? prev.TT_3305

        : stream2;


      return {

        ...prev,

        TT_3303:

          Number(

            (

              currentTT3303 +

              (

                targetTT3303 -

                currentTT3303

              ) * 0.08

            ).toFixed(2)

          ),

        TT_3305:

          Number(

            (

              currentTT3305 +

              (

                targetTT3305 -

                currentTT3305

              ) * 0.08

            ).toFixed(2)

          )

      };

    });

  }, 1000);


  return () => {

    console.log("❌ E-113 interval cleared");

    if (E113_intervalRef.current) {

      clearInterval(E113_intervalRef.current);

      E113_intervalRef.current = null;

    }

    E113_isMounted.current = false;

  };

}, []);






/* =========================================================
              SPLITTING MAIN DIESEL LINE
========================================================= */

const openDieselBranchCount =
[
  xv3302Open,
  xv3303Open
].filter(Boolean).length;

const FIC_3302 =
  xv3302Open
    ? FIC_3301 / openDieselBranchCount
    : 0;

const FIC_3303 =
  xv3303Open
    ? FIC_3301 / openDieselBranchCount
    : 0;









      const TT_3306 = xv3304Open ? 27.564 : 22;
      const TT_3307 = TT_11006;
      const FIC_3307 = FIC_2308;
      const PT_3307 = PT_2308 *0.5;
      const PT_3309 = PT_3307 * 0.8;



      

      /* =========================================================
   E-117A/B
   ATMOSPHERIC RESIDUE ↔ COOLING WATER COOLER
========================================================= */

const TT_3308 = processData.TT_3308;
const TT_3309 = processData.TT_3309;

/* =========================================================
   REFS
========================================================= */

const E117_intervalRef = useRef(null);
const E117_isMounted = useRef(false);

const E117_processRef = useRef({});

/* =========================================================
   KEEP LIVE VALUES UPDATED
========================================================= */

useEffect(() => {

  E117_processRef.current = {

    TT_3306,
    TT_3307,
    xv3301Open

  };

});

/* =========================================================
   E-117 COOLER
========================================================= */

useEffect(() => {

  if (E117_isMounted.current) return;

  E117_isMounted.current = true;

  console.log("✅ E-117 interval created");

  E117_intervalRef.current = setInterval(() => {

    const {

      TT_3306,
      TT_3307,
      xv3301Open

    } = E117_processRef.current;

    /* =====================================
       INLETS
    ===================================== */

    const waterInlet =
      Number(TT_3306) || 25;

    const residueInlet =
      Number(TT_3307) || 25;

    let targetTT3308 = waterInlet;
    let targetTT3309 = residueInlet;

    /* =====================================
       HEAT EXCHANGE
    ===================================== */

    if (

      xv3301Open &&
      residueInlet > waterInlet + 5

    ) {

      const deltaT =
        residueInlet - waterInlet;

      /*
        Overall exchanger effectiveness
      */

      const effectiveness = 0.72;

      /*
        Residue cooling
      */

      const residueLoss = Math.min(

        35,

        deltaT * effectiveness

      );

      /*
        Cooling water heating
      */

      const waterGain = Math.min(

        20,

        residueLoss * 0.55

      );

      targetTT3309 =
        residueInlet - residueLoss;

      targetTT3308 =
        waterInlet + waterGain;

      /* ===============================
         PHYSICAL LIMITS
      =============================== */

      targetTT3309 = Math.max(

        targetTT3309,

        waterInlet + 5

      );

      targetTT3308 = Math.min(

        targetTT3308,

        targetTT3309 - 5

      );

    }

    /* =====================================
       THERMAL LAG
    ===================================== */

    setProcessData(prev => ({

      ...prev,

      TT_3308: Number(

        (

          (prev.TT_3308 ?? waterInlet)

          +

          (

            targetTT3308 -

            (prev.TT_3308 ?? waterInlet)

          ) * 0.08

        ).toFixed(2)

      ),

      TT_3309: Number(

        (

          (prev.TT_3309 ?? residueInlet)

          +

          (

            targetTT3309 -

            (prev.TT_3309 ?? residueInlet)

          ) * 0.08

        ).toFixed(2)

      )

    }));

  }, 1000);

  return () => {

    console.log("❌ E-117 interval cleared");

    if (E117_intervalRef.current) {

      clearInterval(E117_intervalRef.current);

      E117_intervalRef.current = null;

    }

    E117_isMounted.current = false;

  };

}, []);







/* =========================================================
              SPLITTING MAIN NAPHTHA LINE
========================================================= */

const openResidueBranchCount =
[
  xv3305Open,
  xv3306Open
].filter(Boolean).length;

const FIC_33010 =
  xv3306Open
    ? FIC_3307 / openResidueBranchCount
    : 0;

const FIC_33011 =
  xv3305Open
    ? FIC_3307 / openResidueBranchCount
    : 0;


    const TT_33011 = xv3305Open ? TT_3309 : 25;
    const TT_33010 = xv3306Open ? TT_3309 : 25;
    const PT_33011 = xv3305Open ? PT_3309 : 0.03;
    const PT_33010 = xv3306Open ? PT_3309 : 0.03;


    
    const TT_33021 = xv3302Open ? TT_3303 : 25;
    const TT_33031 = xv3303Open ? TT_3303 : 25;
    const PT_33021 = xv3302Open ? PT_3302 : 0.03;
    const PT_33031 = xv3303Open ? PT_3302 : 0.03;












  // ✅ FIXED: Update PlantStore without causing re-renders
  useEffect(() => {
    const current = PlantStore.get().transmitters;
    
    if (
      current.TT_3303 === TT_3303 &&
      current.TT_33011 === TT_33011 &&
      current.TT_33010 === TT_33010 &&
      current.PT_3302 === PT_3302 &&
      current.PT_33011 === PT_33011 &&
      current.PT_33010 === PT_33010 &&
      current.FIC_33010 === FIC_33010 &&
      current.FIC_33011 === FIC_33011 &&
      current.FIC_3302 === FIC_3302 &&
      current.FIC_3303 === FIC_3303 &&
      current.FIC_3301 === FIC_3301 &&
      current.TT_33021 === TT_33021 &&
      current.TT_33031 === TT_33031 &&
      current.PT_33021 === PT_33021 &&
      current.PT_33031 === PT_33031
    ) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        TT_3303, PT_3302, FIC_3301, FIC_33011, TT_33011, PT_33011, FIC_33010, TT_33010, PT_33011, FIC_3302, TT_33021, PT_33021, FIC_3303, TT_33031, PT_33031 
      }
    });
  }, [TT_3303, PT_3302, FIC_3301, FIC_33011, TT_33011, PT_33011, FIC_33010, TT_33010, PT_33011, FIC_3302, TT_33021, PT_33021, FIC_3303, TT_33031, PT_33031]);

  return (
    <div className="DieselCooler-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 293px",
        zIndex: 10,
      }}>
        DIESEL COOLER
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-3301"
        tag="XV_3301"
        x={250}
        y={296}
        isClosed={valves.XV_3301}
        toggle={toggleValve}
        r={90}
      />

      <Valve
        className="XV-3302"
        tag="XV_3302"
        x={1050}
        y={393}
        isClosed={valves.XV_3302}
        toggle={toggleValve}
      />

      <Valve
        className="XV-3303"
        tag="XV_3303"
        x={1050}
        y={473}
        isClosed={valves.XV_3303}
        toggle={toggleValve}
      />

      <Valve
        className="XV-3304"
        tag="XV_3304"
        x={350}
        y={873}
        isClosed={valves.XV_3304}
        toggle={toggleValve}
      />

      <Valve
        className="XV-3305"
        tag="XV_3305"
        x={1050}
        y={760}
        isClosed={valves.XV_3305}
        toggle={toggleValve}
      />

      <Valve
        className="XV-3306"
        tag="XV_3306"
        x={1050}
        y={871}
        isClosed={valves.XV_3306}
        toggle={toggleValve}
      />

      {renderInput({
        name: "A-103A Loovers",
        value: A101A_Loovers,
        setter: setA101A_Loovers,
        x: 790,
        y: 170,
        bg: "#d7f1d7",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-103B Loovers",
        value: A101B_Loovers,
        setter: setA101B_Loovers,
        x: 1020,
        y: 170,
        bg: "#bde7bd",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "A-103C Loovers",
        value: A101C_Loovers,
        setter: setA101C_Loovers,
        x: 1280,
        y: 170,
        bg: "#abd6ab",
        w: 50,
        h: 20,
      })}


      {renderInput({
        name: "ATM Air",
        value: ATM_Air,
        setter: setATM_Air,
        x: 1250,
        y: 70,
        bg: "#6584a1",
        w: 50,
        h: 20,
      })}

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= ✅ AIR COOLER FACEPLATES ================= */}
      {openCoolerPanels.map((tag, index) => (
        <AirCoolerFaceplate
          key={tag}
          tag={tag}
          close={() => closeCoolerPanel(tag)}
          defaultPosition={{
            x: 400 + index * 40,
            y: 200 + index * 40
          }}
        />
      ))}

      <Transmitter
        tag="TT_3301"
        value={[TT_3301]}
        x={212}
        y={80}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-103 A-D"
      />

      <Transmitter
        tag="TT_3302"
        value={[TT_3302]}
        x={633}
        y={396}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CONDENSED NAPHTHA & NON CONDENSABLE GASES FROM A-103 A-D TO V-101"
      />

      <Transmitter
        tag="PT_3301"
        value={[PT_3301]}
        x={352}
        y={80}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-103 A-D"
      />

      <Transmitter
        tag="FIC_3301"
        value={[FIC_3301]}
        x={492}
        y={80}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF OVERHEAD VAPOR OF PREFLASH TOWER TO A-103A-D TO A-103 A-D"
      />

      <Transmitter
        tag="FIC_3302"
        value={[FIC_3302]}
        x={1102}
        y={383}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF DIESEL FROM E-117 TO UNQUALIFIED LINE"
      />

      <Transmitter
        tag="FIC_3303"
        value={[FIC_3303]}
        x={1102}
        y={459}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF DIESEL FROM E-117 TO DIESEL STORAGE TANK"
      />

      <Transmitter
        tag="FIC_3307"
        value={[FIC_3307]}
        x={762}
        y={525}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF ATMOSPHERIC RESIDUE FROM E-104A/B TO E-117A/B"
      />

      <Transmitter
        tag="FIC_33010"
        value={[FIC_33010]}
        x={1112}
        y={855}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF ATMOSPHERIC RESIDUE FROM E-117A/B TO RESIDUE STORAGE TANKS"
      />

      <Transmitter
        tag="FIC_33011"
        value={[FIC_33011]}
        x={1112}
        y={750}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF ATMOSPHERIC RESIDUE FROM E-117A/B TO RESIDUE STORAGE TANKS"
      />

      <Transmitter
        tag="TT_3304"
        value={[TT_3304]}
        x={162}
        y={246}
        unit="°C"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER SUPPLY TO E-113 (PREFLASH OVHD VAPOR CONDENSER)"
      />

      <Transmitter
        tag="TT_3306"
        value={[TT_3306]}
        x={232}
        y={856}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER SUPPLY TO E-117A/B"
      />

      <Transmitter
        tag="TT_3305"
        value={[TT_3305]}
        x={162}
        y={452}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FRESH WATER RETURN OUT OF E-117A/B TO WATER RETURN MANIFOLD"
      />

      <Transmitter
        tag="TT_3303"
        value={[TT_3303]}
        x={562}
        y={461}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CONDENSED NAPHTHA & NON CONDENSABLE GASES FROM A-103 A-D AND PREFLASH NAPHTHA AFTER COOLER TO V-101"
      />

      <Transmitter
        tag="TT_3307"
        value={[TT_3307]}
        x={962}
        y={525}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATMOSPHERIC RESIDUE FROM E-104A/B TO E-117A/B"
      />

      <Transmitter
        tag="TT_3308"
        value={[TT_3308]}
        x={322}
        y={543}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF HOT WATER RETURN OUT OF E-117A/B TO WATER RETURN MANIFOLD"
      />

      <Transmitter
        tag="TT_3309"
        value={[TT_3309]}
        x={762}
        y={855}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATMOSPHERIC RESIDUE AT OUTLET OF E-117A/B SHELL SIDE"
      />

      <Transmitter
        tag="PT_3302"
        value={[PT_3302]}
        x={692}
        y={461}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF OVERHEAD VAPOR AT THE TOP OF PREFLASH FRACTIONATION TOWER (T-101) TO A-103 A-D"
      />

      <Transmitter
        tag="PT_3307"
        value={[PT_3307]}
        x={862}
        y={525}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF ATMOSPHERIC RESIDUE FROM E-104A/B TO E-117A/B"
      />

      <Transmitter
        tag="PT_3309"
        value={[PT_3309]}
        x={662}
        y={855}
        unit="MPa"
        max={1}
        HH={0.8}
        H={0.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF ATMOSPHERIC RESIDUE AT OUTLET OF E-117A/B SHELL SIDE"
      />

      <AirCooler
        tag="M103A_A"
        x={676}
        y={285}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M103A_A")}
      />

      <AirCooler
        tag="M103A_B"
        x={723}
        y={285}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M103A_B")}
      />

      <AirCooler
        tag="M103B_A"
        x={917}
        y={285}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M103B_A")}
      />

      <AirCooler
        tag="M103B_B"
        x={964}
        y={285}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M103B_B")}
      />

      <AirCooler
        tag="M103C_A"
        x={1171}
        y={285}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M103C_A")}
      />

      <AirCooler
        tag="M103C_B"
        x={1217}
        y={285}
        rpm={1500}
        size={48}
        bladeCount={7}
        bladeLength={30}
        onClick={() => openCoolerPanel("M103C_B")}
      />


    </div>
  );
}

export default memo(DieselCooler);